import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Chip, 
  CircularProgress, 
  Alert,
  Tabs,
  Tab,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Euro, 
  LocalShipping, 
  CheckCircle, 
  PauseCircle,
  Assessment,
  TrendingUp,
  CalendarToday,
  Inventory
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';

const CODDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [timingAnalytics, setTimingAnalytics] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  
  // Date filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState('this_month');
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Detailed orders
  const [detailedOrders, setDetailedOrders] = useState([]);
  const [detailedLoading, setDetailedLoading] = useState(false);

  // Date range options
  const dateRangeOptions = [
    { value: 'all_time', label: 'All Time' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Maximum allowed date range (6 months)
  const MAX_DATE_RANGE_DAYS = 180;

  // Calculate date range based on selection
  const getDateRange = useCallback((range) => {
    const now = new Date();
    switch (range) {
      case 'this_week':
        return {
          start: startOfWeek(now, { weekStartsOn: 1 }), // Monday
          end: endOfWeek(now, { weekStartsOn: 1 })
        };
      case 'last_week':
        const lastWeek = subWeeks(now, 1);
        return {
          start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          end: endOfWeek(lastWeek, { weekStartsOn: 1 })
        };
      case 'this_month':
        return {
          start: startOfMonth(now),
          end: endOfMonth(now)
        };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth)
        };
      case 'last_3_months':
        return {
          start: startOfMonth(subMonths(now, 2)),
          end: endOfMonth(now)
        };
      case 'last_6_months':
        return {
          start: startOfMonth(subMonths(now, 5)),
          end: endOfMonth(now)
        };
      case 'custom':
        return { start: startDate, end: endDate };
      default:
        return { start: null, end: null };
    }
  }, [startDate, endDate]);

  // Fetch COD analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams();
      const dateRange = getDateRange(selectedDateRange);
      
      if (dateRange.start) params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      if (dateRange.end) params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      
      const response = await fetch(`/api/cod/dashboard?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch COD analytics');
      }
      
      const data = await response.json();
      setAnalytics(data.analytics);
      setTimingAnalytics(data.timing);
      setOrderDetails(data.order_details);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDateRange, getDateRange]);

  // Fetch detailed orders for a specific status
  const fetchDetailedOrders = async (status) => {
    try {
      setDetailedLoading(true);
      
      const params = new URLSearchParams({ status });
      const dateRange = getDateRange(selectedDateRange);
      
      if (dateRange.start) params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      if (dateRange.end) params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      
      const response = await fetch(`/api/cod/orders?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch detailed orders');
      }
      
      const data = await response.json();
      setDetailedOrders(data.orders);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailedLoading(false);
    }
  };

  useEffect(() => {
    // Only run once on component mount to initialize
    if (!isInitialized) {
      setIsInitialized(true);
      fetchAnalytics();
    }
  }, [fetchAnalytics, isInitialized]);

  // Re-fetch when date range changes
  useEffect(() => {
    if (selectedDateRange !== 'custom') {
      fetchAnalytics();
    }
  }, [selectedDateRange, fetchAnalytics]);

  // Handle date range selection change
  const handleDateRangeChange = (newRange) => {
    setSelectedDateRange(newRange);
    if (newRange === 'custom') {
      // Reset custom dates when switching to custom mode
      setStartDate(null);
      setEndDate(null);
    }
  };

  // Validate date range
  const validateDateRange = (start, end) => {
    if (!start || !end) return { valid: false, error: 'Both start and end dates are required' };
    
    if (start > end) return { valid: false, error: 'Start date cannot be after end date' };
    
    const diffInDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      return { valid: false, error: `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days (6 months)` };
    }
    
    return { valid: true, error: null };
  };

  // Handle custom date filter changes
  const handleCustomDateFilterChange = () => {
    if (selectedDateRange === 'custom') {
      const validation = validateDateRange(startDate, endDate);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      setError('');
      fetchAnalytics();
    }
  };

  // Get current date range for display
  const getCurrentDateRangeDisplay = () => {
    const range = getDateRange(selectedDateRange);
    if (range.start && range.end) {
      return `${format(range.start, 'dd/MM/yyyy')} - ${format(range.end, 'dd/MM/yyyy')}`;
    }
    return dateRangeOptions.find(opt => opt.value === selectedDateRange)?.label || 'All Time';
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Get status chip color
  const getStatusChipColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'in_escrow': return 'info';
      case 'pending_delivery': return 'warning';
      case 'in_transit': return 'warning';
      case 'preparing': return 'default';
      case 'on_hold': return 'warning'; // Changed from 'error' to 'warning' for orange color
      default: return 'default';
    }
  };



  // Status cards data
  const statusCards = analytics ? [
    {
      title: 'Paid',
      amount: analytics.amount_paid,
      count: analytics.orders_paid,
      percentage: analytics.paid_percentage,
      icon: <CheckCircle sx={{ fontSize: 28 }} />,
      color: '#4caf50',
      description: analytics.avg_days_to_delivery > 0 
        ? `Avg: ${analytics.avg_days_to_delivery} days to delivery`
        : 'BRT/GLS payments received',
      status: 'paid'
    },
    {
      title: 'Escrow',
      amount: analytics.amount_in_escrow,
      count: analytics.orders_in_escrow,
      percentage: analytics.escrow_percentage,
      icon: <Euro sx={{ fontSize: 28 }} />,
      color: '#2196f3',
      description: 'Delivered, awaiting payment',
      status: 'in_escrow'
    },
    {
      title: 'In Transit',
      amount: analytics.amount_pending_delivery,
      count: analytics.orders_pending_delivery,
      percentage: analytics.pending_delivery_percentage,
      icon: <LocalShipping sx={{ fontSize: 28 }} />,
      color: '#ff9800',
      description: 'Shipped, on the way',
      status: 'pending_delivery'
    },
    {
      title: 'Preparing',
      amount: analytics.amount_preparing,
      count: analytics.orders_preparing,
      percentage: analytics.preparing_percentage,
      icon: <Inventory sx={{ fontSize: 28 }} />,
      color: '#9e9e9e',
      description: 'In warehouse, not shipped',
      status: 'preparing'
    },
    {
      title: 'On Hold',
      amount: analytics.amount_on_hold,
      count: analytics.orders_on_hold,
      percentage: analytics.on_hold_percentage,
      icon: <PauseCircle sx={{ fontSize: 28 }} />,
      color: '#fb923c',
      description: analytics.avg_days_to_hold > 0 
        ? `Avg: ${analytics.avg_days_to_hold} days to hold`
        : 'Currently held by carrier',
      status: 'on_hold'
    }
  ] : [];

  const tabLabels = ['Overview', 'Paid Orders', 'Escrow', 'In Transit', 'Preparing', 'On Hold'];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assessment color="primary" />
          COD Cashflow
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Compact Date Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <CalendarToday color="primary" />
              <Typography variant="h6">
                Date Range: {getCurrentDateRangeDisplay()}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={selectedDateRange}
                  label="Period"
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                >
                  {dateRangeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedDateRange === 'custom' && (
                <>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    renderInput={(params) => <TextField {...params} size="small" sx={{ width: 140 }} />}
                    maxDate={new Date()}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    renderInput={(params) => <TextField {...params} size="small" sx={{ width: 140 }} />}
                    maxDate={new Date()}
                    minDate={startDate}
                  />
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={handleCustomDateFilterChange}
                    disabled={!startDate || !endDate}
                  >
                    Apply
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', ml: 1 }}>
                    Max: 6 months
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statusCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 }
                }}
                onClick={() => {
                  setActiveTab(index + 1);
                  fetchDetailedOrders(card.status);
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Avatar sx={{ bgcolor: card.color, width: 40, height: 40 }}>
                      {card.icon}
                    </Avatar>
                    <Box textAlign="right">
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                        {formatCurrency(card.amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {card.count} orders ({card.percentage || 0}%)
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Total Summary */}
        {analytics && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>
              Total COD Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <TrendingUp color="success" sx={{ fontSize: 24 }} />
                  <Box>
                    <Typography variant="h5" color="success.main" sx={{ fontSize: '1.4rem', lineHeight: 1.2 }}>
                      {formatCurrency(analytics.total_cod_amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total COD Value
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Assessment color="primary" sx={{ fontSize: 24 }} />
                  <Box>
                    <Typography variant="h5" color="primary.main" sx={{ fontSize: '1.4rem', lineHeight: 1.2 }}>
                      {analytics.total_cod_orders}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total COD Orders
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Payment Timing Insights */}
        {timingAnalytics && (timingAnalytics.avg_days_to_payout > 0 || Object.values(timingAnalytics.delivery_success_rate || {}).some(d => d.total > 0)) && (
          <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#fafafa', border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              💰 Payment Cycle Insights
            </Typography>
            
            <Grid container spacing={3}>
              {/* Left Column: Payment Cycle */}
              {timingAnalytics.avg_days_to_payout > 0 && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ pr: { md: 2 }, borderRight: { md: '1px solid #e0e0e0' } }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                      Payment Cycle
                    </Typography>
                    
                    {/* Summary */}
                    <Typography variant="body2" sx={{ mb: 2, fontSize: '0.85rem' }}>
                      Avg: <strong>{timingAnalytics.avg_days_to_payout} days</strong> | 
                      Median: <strong>{timingAnalytics.median_days_to_payout} days</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, fontSize: '0.75rem' }}>
                      Range: {timingAnalytics.fastest_payout_days}-{timingAnalytics.slowest_payout_days} days
                    </Typography>
                    
                    {/* Distribution */}
                    <Typography variant="caption" gutterBottom sx={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', mb: 1 }}>
                      Distribution:
                    </Typography>
                    <Box sx={{ pl: 0.5 }}>
                      {timingAnalytics.payout_distribution && Object.entries(timingAnalytics.payout_distribution).map(([key, data]) => (
                        <Box key={key} display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Box 
                            sx={{ 
                              width: `${Math.max(data.percentage * 2, 4)}px`, 
                              height: 8, 
                              bgcolor: 'primary.main', 
                              borderRadius: 1 
                            }} 
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                            <strong>
                              {key === 'days_0_10' ? '0-10d' : 
                               key === 'days_11_15' ? '11-15d' :
                               key === 'days_16_20' ? '16-20d' :
                               key === 'days_21_25' ? '21-25d' :
                               key === 'days_26_30' ? '26-30d' : '30+d'}
                            </strong>: 
                            {' '}{data.count} ({formatCurrency(data.amount)}) - {data.percentage}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              )}
              
              {/* Right Column: Delivery Success Rate */}
              {timingAnalytics.delivery_success_rate && Object.values(timingAnalytics.delivery_success_rate).some(d => d.total > 0) && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ pl: { md: 2 } }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                      Delivery Success Rate
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontSize: '0.75rem' }}>
                      Days from fulfillment to delivery
                    </Typography>
                    
                    {/* Overall Breakdown */}
                    {timingAnalytics.overall_paid_percentage > 0 && (
                      <Typography variant="body2" sx={{ mb: 2, fontSize: '0.85rem', fontWeight: 500 }}>
                        Overall: <span style={{ color: '#4caf50' }}>✅ {timingAnalytics.overall_paid_percentage}% Paid</span>
                        {' '}|{' '}
                        <span style={{ color: '#ff9800' }}>⚠️ {timingAnalytics.overall_hold_percentage}% Hold</span>
                      </Typography>
                    )}
                    
                    <Box>
                      {Object.entries(timingAnalytics.delivery_success_rate)
                        .filter(([, data]) => data.total > 0)
                        .map(([key, data]) => (
                          <Typography key={key} variant="caption" display="block" sx={{ mb: 0.8, fontSize: '0.75rem', lineHeight: 1.4 }}>
                            <strong>
                              {key === 'delivered_0_7_days' ? '0-7 days' :
                               key === 'delivered_8_14_days' ? '8-14 days' :
                               key === 'delivered_15_21_days' ? '15-21 days' : '22-30 days'}:
                            </strong>
                            <br />
                            <span style={{ color: '#4caf50' }}>✅ {data.paid_pct}% Paid ({data.paid})</span>
                            {' '}|{' '}
                            <span style={{ color: '#ff9800' }}>⚠️ {data.hold_pct}% Hold ({data.hold})</span>
                            {' '}of {data.total}
                          </Typography>
                        ))}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Detailed View Tabs */}
        <Paper>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              if (newValue > 0) {
                const statusMap = ['', 'paid', 'in_escrow', 'pending_delivery', 'preparing', 'on_hold'];
                fetchDetailedOrders(statusMap[newValue]);
              }
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>

          <Box p={3}>
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Recent Orders by Category
                </Typography>
                {Object.entries(orderDetails).map(([category, orders]) => (
                  orders.length > 0 && (
                    <Box key={category} mb={3}>
                      <Typography variant="subtitle1" gutterBottom sx={{ textTransform: 'capitalize' }}>
                        {category.replace('_', ' ')} ({orders.length} orders)
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order #</TableCell>
                              <TableCell>Customer Email</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Payout Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orders.map((order, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{order.shopify_order_number}</TableCell>
                                <TableCell>{order.customer_email || '-'}</TableCell>
                                <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={order.payment_status}
                                    color={getStatusChipColor(order.payment_status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {format(parseISO(order.created_at), 'dd/MM/yyyy')}
                                </TableCell>
                                <TableCell>
                                  {order.payout_date ? format(parseISO(order.payout_date), 'dd/MM/yyyy') : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )
                ))}
              </Box>
            )}

            {activeTab > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {tabLabels[activeTab]} Details
                </Typography>
                {detailedLoading ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order #</TableCell>
                          <TableCell>Customer Email</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Order Status</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Payout Date</TableCell>
                          <TableCell>Hold Info</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailedOrders.map((order, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{order.shopify_order_number}</TableCell>
                            <TableCell>{order.customer_email || '-'}</TableCell>
                            <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={order.status || 'Unknown'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {format(parseISO(order.created_at), 'dd/MM/yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              {order.payout_date ? format(parseISO(order.payout_date), 'dd/MM/yyyy') : '-'}
                            </TableCell>
                            <TableCell>
                              {order.hold_date && (
                                <Box>
                                  <Typography variant="caption" display="block">
                                    Hold: {format(parseISO(order.hold_date), 'dd/MM/yyyy')}
                                  </Typography>
                                  {order.hold_reason && (
                                    <Typography variant="caption" color="text.secondary">
                                      {order.hold_reason}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default CODDashboard;
